<script lang="ts" setup>
import { computed, toRef } from 'vue';

import { useMediaRetry } from '#nora-chat-ui/composables/useMediaRetry';

interface MessageAttachmentProps {
  maxSize: number;
  name: string;
  url: string;
}

const props = defineProps<MessageAttachmentProps>();
const emit = defineEmits<{
  refreshUrls: [];
}>();

const urlRef = toRef(props, 'url');

const {
  isError,
  isLoading,
  handleError,
  handleSuccess,
} = useMediaRetry({
  urlRef,
  onRefreshRequest: () => emit('refreshUrls'),
});

const height = computed(() => {
  return props.maxSize;
});
</script>

<template>
  <div
    class="relative"
    :style="{ height: `${height}px` }"
  >
    <div v-if="isError" class="flex items-center justify-center w-full h-full bg-gray-200 text-gray-500">
      <span class="text-sm wrap-break-word">No preview</span>
    </div>
    <Transition name="fade">
      <img
        v-show="!isLoading && !isError"
        class="object-center w-full h-full object-cover"
        :src="url"
        :alt="name"
        @load="handleSuccess"
        @error="handleError"
      >
    </Transition>
    <Transition name="fade">
      <div
        v-show="isLoading"
        class="skeleton w-full h-full"
      />
    </Transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.skeleton {
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #e0e0e0 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s ease-in-out infinite;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
